import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import PropertiesPage from "./pages/PropertiesPage";
import MetersPage from "./pages/MetersPage";
import BuyTokensPage from "./pages/BuyTokensPage";
import PaymentsPage from "./pages/PaymentsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import AutoDeductionsPage from "./pages/AutoDeductionsPage";
import RecurringBillingPage from "./pages/RecurringBillingPage";
import TenantAnalyticsPage from "./pages/TenantAnalyticsPage";
import TenantsPage from "./pages/TenantsPage";
import CreditBillingPage from "./pages/CreditBillingPage";
import CreditManagementPage from "./pages/CreditManagementPage";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  return user ? <Layout>{children}</Layout> : <Navigate to="/login" replace />;
}

function RoleRoute({
  roles,
  children,
}: {
  roles: string[];
  children: React.ReactNode;
}) {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties"
          element={
            <RoleRoute roles={["super_admin", "property_manager"]}>
              <PropertiesPage />
            </RoleRoute>
          }
        />
        <Route
          path="/meters"
          element={
            <ProtectedRoute>
              <MetersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/buy-tokens"
          element={
            <RoleRoute roles={["tenant"]}>
              <BuyTokensPage />
            </RoleRoute>
          }
        />
        <Route
          path="/payments"
          element={
            <ProtectedRoute>
              <PaymentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <RoleRoute roles={["super_admin", "property_manager"]}>
              <AnalyticsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/auto-deductions"
          element={
            <RoleRoute roles={["tenant"]}>
              <AutoDeductionsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/recurring-billing"
          element={
            <RoleRoute roles={["tenant"]}>
              <RecurringBillingPage />
            </RoleRoute>
          }
        />
        <Route
          path="/my-analytics"
          element={
            <RoleRoute roles={["tenant"]}>
              <TenantAnalyticsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/tenants"
          element={
            <RoleRoute roles={["super_admin", "property_manager"]}>
              <TenantsPage />
            </RoleRoute>
          }
        />

        <Route
          path="/credit-billing"
          element={
            <RoleRoute roles={["tenant"]}>
              <CreditBillingPage />
            </RoleRoute>
          }
        />
        <Route
          path="/credit-management"
          element={
            <RoleRoute roles={["super_admin", "property_manager"]}>
              <CreditManagementPage />
            </RoleRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
